import Modal from "/WebInterface/new-ui/modules/modal.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { doLogout } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";

/**
 * Creates and shows a modal for getUserInfo errors with detailed debug information
 * @param {Error} error - The error that occurred
 * @param {Object} options - Additional options
 */
export const showGetUserInfoErrorModal = (error, options = {}) => {
  const {
    onReload = () => window.location.reload(),
    onLogout = () => doLogout(),
    showDebugInfo = false,
  } = options;

  // Collect debug information
  const debugInfo = collectDebugInfo(error);

  // Create modal content
  const modalContent = createModalContent();

  // Create modal buttons
  const buttons = createModalButtons(debugInfo, onReload, onLogout);

  // Create and show modal
  const modal = new Modal({
    title: t("getUserInfoErrorTitle"),
    body: modalContent,
    buttons,
    style: "card",
    closable: false,
    hasCloseOption: false,
    hasFooter: true,
    hasHeader: true,
    show: true,
    destroyOnClose: true,
    backdrop: true,
    zIndex: "9999",
  });

  return modal;
};

/**
 * Collects comprehensive debug information
 * @param {Error} error - The error that occurred
 * @returns {Object} Debug information object
 */
const collectDebugInfo = (error) => {
  const now = new Date();
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  return {
    timestamp: {
      value: now.toISOString(),
      description: t("saveTimeDescription"),
    },
    errorDetails: {
      type: error?.constructor?.name || "Unknown",
      message: error?.message || "Unknown error",
      stack: error?.stack || "",
      code: error?.code || "",
      statusCode: error?.status || error?.statusCode || "",
      responseText: error?.responseText || error?.response || "",
    },
    browserInfo: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : false,
    },
    networkInfo: connection
      ? {
          effectiveType: connection.effectiveType || "unknown",
          downlink: connection.downlink || "unknown",
          rtt: connection.rtt || "unknown",
          saveData: connection.saveData || false,
        }
      : null,
    sessionInfo: {
      currentUrl: window.location.href,
      referrer: document.referrer,
      title: document.title,
      readyState: document.readyState,
    },
    storageInfo: {
      localStorage: Object.keys(localStorage).length,
      sessionStorage: Object.keys(sessionStorage).length,
      cookies: document.cookie ? document.cookie.split(";").length : 0,
    },
    performanceInfo: {
      navigationStart:
        performance.timing?.navigationStart || performance.timeOrigin,
      loadEventEnd: performance.timing?.loadEventEnd || 0,
      domContentLoadedEventEnd:
        performance.timing?.domContentLoadedEventEnd || 0,
    },
  };
};

/**
 * Creates the modal content HTML
 * @param {Object} debugInfo - Debug information object
 * @param {boolean} showDebugInfo - Whether to show debug info section
 * @returns {string} HTML content for the modal
 */
const createModalContent = () => {
  const errorMessage = t("getUserInfoErrorMessage");
  return `
    <div class="content">
      <p class="mb-4">${errorMessage}</p>
    </div>
  `;
};

/**
 * Creates the debug information section
 * @param {Object} debugInfo - Debug information object
 * @returns {string} HTML for debug info section
 */
const createDebugInfoSection = (debugInfo) => {
  return `
    <div class="debug-info-section mt-4">
      <h4 class="title is-5 mb-3">${t("debugInfoTitle")}</h4>
      <p class="mb-3">${t("debugInfoDescription")}</p>

      <div class="debug-info-content" style="max-height: 300px; overflow-y: auto; background: #f5f5f5; padding: 1rem; border-radius: 4px; font-family: monospace; font-size: 12px; white-space: pre-wrap;">
${formatDebugInfo(debugInfo)}
      </div>

      <div class="mt-3">
        <small class="has-text-grey">${t("altClickToCopy")}</small>
      </div>
    </div>
  `;
};

/**
 * Formats debug information for display
 * @param {Object} debugInfo - Debug information object
 * @returns {string} Formatted debug information
 */
const formatDebugInfo = (debugInfo) => {
  const sections = [
    {
      title: "Error Details",
      data: {
        Timestamp: debugInfo.timestamp.value,
        "Error Type": debugInfo.errorDetails.type,
        "Error Message": debugInfo.errorDetails.message,
        "Error Code": debugInfo.errorDetails.code || "N/A",
        "Status Code": debugInfo.errorDetails.statusCode || "N/A",
        "Error Stack": debugInfo.errorDetails.stack || "N/A",
      },
    },
    {
      title: "Browser Info",
      data: {
        "User Agent": debugInfo.browserInfo.userAgent,
        Language: debugInfo.browserInfo.language,
        Platform: debugInfo.browserInfo.platform,
        Online: debugInfo.browserInfo.onLine ? "Yes" : "No",
        "Cookies Enabled": debugInfo.browserInfo.cookieEnabled ? "Yes" : "No",
      },
    },
    {
      title: "Session Info",
      data: {
        "Current URL": debugInfo.sessionInfo.currentUrl,
        Referrer: debugInfo.sessionInfo.referrer || "N/A",
        Title: debugInfo.sessionInfo.title,
        "Ready State": debugInfo.sessionInfo.readyState,
      },
    },
  ];

  if (debugInfo.networkInfo) {
    sections.push({
      title: "Network Info",
      data: {
        "Connection Type": debugInfo.networkInfo.effectiveType,
        Downlink: debugInfo.networkInfo.downlink,
        RTT: debugInfo.networkInfo.rtt,
        "Save Data": debugInfo.networkInfo.saveData ? "Yes" : "No",
      },
    });
  }

  sections.push({
    title: "Storage Info",
    data: {
      "Local Storage": `${debugInfo.storageInfo.localStorage} items`,
      "Session Storage": `${debugInfo.storageInfo.sessionStorage} items`,
      Cookies: `${debugInfo.storageInfo.cookies} cookies`,
    },
  });

  sections.push({
    title: "Performance Info",
    data: {
      "Navigation Start": debugInfo.performanceInfo.navigationStart,
      "Load Event End": debugInfo.performanceInfo.loadEventEnd,
      "DOM Content Loaded Event End":
        debugInfo.performanceInfo.domContentLoadedEventEnd,
    },
  });

  return sections
    .map((section) => {
      const sectionData = Object.entries(section.data)
        .map(([key, value]) => `${key}: ${value}`)
        .join("\n");

      return `${section.title}:\n${sectionData}\n`;
    })
    .join("\n");
};

/**
 * Creates modal buttons with copy debug info functionality
 * @param {Object} debugInfo - Debug information object
 * @param {Function} onReload - Reload page callback
 * @param {Function} onLogout - Logout callback
 * @returns {Array} Array of button configurations
 */
const createModalButtons = (debugInfo, onReload, onLogout) => {
  const copyDebugInfo = () => {
    const debugText = formatDebugInfo(debugInfo);
    navigator.clipboard
      .writeText(debugText)
      .then(() => {
        growl("Debug info copied to clipboard", "", 3000, false);
      })
      .catch(() => {
        growl("Failed to copy debug info", "", 3000, true);
      });
  };

  return [
    {
      label: "Copy debug info",
      close: false,
      classes: ["is-secondary", "getuserinfo-modal-btn"],
      onClick: copyDebugInfo,
      leftIconClasses: ["fas", "fa-copy"],
    },
    {
      label: t("reloadPage"),
      close: false,
      classes: ["is-primary", "getuserinfo-modal-btn"],
      onClick: onReload,
      leftIconClasses: ["fas", "fa-redo"],
    },
    {
      label: t("Logout"),
      close: false,
      classes: ["is-danger", "getuserinfo-modal-btn"],
      onClick: onLogout,
      leftIconClasses: ["fas", "fa-sign-out-alt"],
    },
  ];
};

export default showGetUserInfoErrorModal;
